import { Avatar, Card, CardContent, CardDate, CardHeader, CardMedia, Chip, useMediaQuery } from '@bi-digital/react';
import type { SearchResult } from '../../types';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CalendarIcon,
    EllipseFilledIcon,
    EllipseHalfIcon,
    LocationIcon,
    MailIcon,
    PhoneIcon,
    WorldIcon,
} from '@bi-digital/icons';
import { SoftHyphen } from '@bino/utils/components';
import { formatDate, formatDateRange, formatPhoneNumber, getInitials } from '../../utils';

import styles from './result.module.css';

const avatarColors = ['primary', 'info', 'success', 'warning', 'alert'] as const;

export type ResultProps = {
    result: SearchResult;
};

export const Result = ({ result }: ResultProps) => {
    const { t, i18n } = useTranslation();
    const isCompact = useMediaQuery('(width < 600px)');
    const isMedium = useMediaQuery('(width < 840px)');
    const titleLinkId = useId();

    // The card delegates its click to the title link, so the link's accessible name
    // stays the title alone instead of swallowing the chips and summary text.
    const titleLink = (
        <a id={titleLinkId} href={result.url}>
            <SoftHyphen text={result.title} />
        </a>
    );

    if (result.type === 'Articles' && result.articleData) {
        const label = t(`article.types.${result.articleData.type}`);
        return (
            <Card orientation='horizontal' data-clickdelegatefor={titleLinkId}>
                <CardContent>
                    <CardHeader
                        header={titleLink}
                        label={label}
                        subLabel={
                            result.articleData.date
                                ? formatDate(new Date(result.articleData.date), i18n.language)
                                : undefined
                        }
                    />
                    <p>{result.articleData.summary}</p>
                </CardContent>
                {result.articleData.image && (
                    <CardMedia>
                        <img alt='' src={result.articleData.image} />
                    </CardMedia>
                )}
            </Card>
        );
    }

    if (result.type === 'Employees' && result.employeeData) {
        return (
            <Card orientation='horizontal' data-clickdelegatefor={titleLinkId}>
                <CardContent className={styles.employeeAvatarWrapper}>
                    <Avatar
                        aria-label={result.title}
                        data-color={avatarColors[result.title.length % avatarColors.length]}
                        size={isMedium ? 'small' : 'medium'}
                    >
                        {result.employeeData.image ? (
                            <img alt={result.title} src={result.employeeData.image} />
                        ) : (
                            getInitials(result.title)
                        )}
                    </Avatar>
                </CardContent>
                <CardContent>
                    <CardHeader
                        description={[result.employeeData.role, result.employeeData.department]
                            .filter(Boolean)
                            .join(', ')}
                        header={titleLink}
                    />
                    <div className={styles.chips}>
                        {result.employeeData.phone && (
                            <Chip
                                size={isCompact ? 'small' : 'medium'}
                                startIcon={<PhoneIcon title={t(`employee.phone`)} />}
                            >
                                {formatPhoneNumber(result.employeeData.phone)}
                            </Chip>
                        )}
                        {result.employeeData.email && (
                            <Chip
                                size={isCompact ? 'small' : 'medium'}
                                startIcon={<MailIcon title={t(`employee.mail`)} />}
                            >
                                {result.employeeData.email}
                            </Chip>
                        )}
                        {result.employeeData.campus && (
                            <Chip
                                size={isCompact ? 'small' : 'medium'}
                                startIcon={<LocationIcon title={t(`employee.location`)} />}
                            >
                                {result.employeeData.campus}
                            </Chip>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (result.type === 'Events' && result.eventData) {
        const fromDate = new Date(result.eventData.from);
        const toDate = new Date(result.eventData.to);
        const month = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(fromDate);
        return (
            <Card orientation={isCompact ? 'vertical' : 'horizontal'} data-clickdelegatefor={titleLinkId}>
                <CardMedia overlay={<CardDate day={fromDate.getDate()} month={month} />}>
                    <img alt='' src={result.eventData.image || '/public/images/fallback_image.webp'} />
                </CardMedia>
                <CardContent>
                    <CardHeader header={titleLink} />
                    <div className={styles.chips}>
                        <Chip
                            size={isCompact ? 'small' : 'medium'}
                            startIcon={<CalendarIcon title={t('event.when')} />}
                        >
                            <span className={styles.truncate}>{formatDateRange(fromDate, toDate, i18n.language)}</span>
                        </Chip>
                        <Chip
                            size={isCompact ? 'small' : 'medium'}
                            startIcon={<LocationIcon title={t('event.where')} />}
                        >
                            <span className={styles.truncate}>{result.eventData.location}</span>
                        </Chip>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (result.type === 'Programmes' && result.programmeData) {
        return (
            <Card orientation='horizontal' data-clickdelegatefor={titleLinkId}>
                <CardContent>
                    <CardHeader
                        header={titleLink}
                        label={
                            result.programmeData.levels.length > 0
                                ? t(`programme.levels.${result.programmeData.levels[0]}`)
                                : undefined
                        }
                        subLabel={
                            result.programmeData.credits > 0
                                ? t('programme.credits', { count: result.programmeData.credits })
                                : undefined
                        }
                    />
                    <div className={styles.chips}>
                        {result.programmeData.loads.map((load) => (
                            <Chip
                                size={isCompact ? 'small' : 'medium'}
                                key={load}
                                startIcon={
                                    load === 'FullTime' ? (
                                        <EllipseFilledIcon title={t(`programme.load`)} />
                                    ) : (
                                        <EllipseHalfIcon title={t(`programme.load`)} />
                                    )
                                }
                            >
                                {t(`programme.loads.${load}`)}
                            </Chip>
                        ))}
                        {result.programmeData.terms.map((term) => (
                            <Chip
                                size={isCompact ? 'small' : 'medium'}
                                key={term}
                                startIcon={<CalendarIcon title={t(`programme.semester`)} />}
                            >
                                {t(`programme.semesters.${term.endsWith('10') ? 'Spring' : 'Fall'}`, {
                                    year: Number(term.slice(0, 4)),
                                })}
                            </Chip>
                        ))}
                        <Chip
                            size={isCompact ? 'small' : 'medium'}
                            startIcon={<WorldIcon title={t('programme.language')} />}
                        >
                            {t(`programme.languages.${result.programmeData.programmeLanguage}`)}
                        </Chip>
                        {result.programmeData.campuses.map((campus) => (
                            <Chip
                                size={isCompact ? 'small' : 'medium'}
                                key={campus}
                                startIcon={<LocationIcon title={t('programme.campus')} />}
                                data-color={campus === 'Online' ? 'info' : undefined}
                            >
                                {t(`programme.campuses.${campus}`)}
                            </Chip>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return null;
};
